import Config from "../../../core/config/Config";
import NGTConfigKey from "../../../ngt/base/NGTConfigKey";
import NGTVariableManager from "../../../ngt/base/NGTVariableManager";
import BackendService from "../../backend/controller/BackendService";
import CharacterTrackerEvent from "../../characters/event/CharacterTrackerEvent";
import CharacterModel from "../../characters/model/CharacterModel";
import CharacterTracker from "../../characters/model/CharacterTracker";
import DebugModel from "../../debug/model/DebugModel";
import PlayerModel from "../../player/model/PlayerModel";
import { StoryVariables } from "../../variables/StoryVariables";
import ConversationEndedEvent from "../event/ConversationEndedEvent";
import ConversationPhaseChangedEvent from "../event/ConversationPhaseChangedEvent";
import ConversationPhaseReasoningEvent from "../event/ConversationPhaseReasoningEvent";
import ConversationRollbackEvent from "../event/ConversationRollbackEvent";
import ConversationStartedEvent from "../event/ConversationStartedEvent";
import ConversationStateChangedEvent from "../event/ConversationStateChangedEvent";
import NewConversationMessagesEvent from "../event/NewConversationMessageEvent";
import Conversation from "../model/Conversation";
import GameConversationMessage, { GameMessageSender } from "../model/GameConversationMessage";
import LlmConversationMessage from "../model/LlmConversationMessage";
import ConversationState from "./state/ConversationState";
import PlayerTurnConversationState from "./state/PlayerTurnConversationState";
import StartingConversationState from "./state/StartingTurnConversationState";

export default class ConversationPlayer {
    /**
     * @constructor
     * @param {soma.EventDispatcher} dispatcher 
     * @param {BackendService} backendService 
     * @param {Config} config 
     * @param {PlayerModel} playerModel
     * @param {NGTVariableManager} variableManager
     */
    constructor(dispatcher, backendService, config, playerModel, variableManager) {
        this._dispatcher = dispatcher;
        this._backendService = backendService;
        this._config = config;
        this._playerModel = playerModel;
        this._variableManager = variableManager;

        /** @type {Conversation} */
        this._conversation = null;
        /** @type {ConversationState} */
        this._currentState = null;
        /** @type {string} */
        this._chapterId = null;

        /** @type {object} */
        this._conversationSnapshotData = null;
        /** @type {ConversationState} */
        this._conversationSnapshotState = null;
        /** @type {Map<string, CharacterTracker[]>} */
        this._conversationCharacterTrackerSnapshot = new Map();
    }

    destroy() {
        this._conversation?.removeAllEventListeners();
    }

    /**
     * 
     * @param {string} chapterId 
     * @param {string} conversationId 
     * @param {CharacterModel[]} characters 
     * @param {LlmConversationMessage[]} llmConversationHistory
     */
    async start(chapterId, conversationId, characters, llmConversationHistory) {
        this._chapterId = chapterId;

        const configuredMaxPlayerTurns = this._variableManager.solve(StoryVariables.MAX_PLAYER_TURNS);
        const defaultMaxPlayerTurns = this._config.getValue(NGTConfigKey.DEFAULT_MAX_PLAYER_TURNS);
        const maxPlayerTurns = DebugModel.maxTurns || configuredMaxPlayerTurns || defaultMaxPlayerTurns;

        this._charactersInConversation = characters;
        this._conversation = new Conversation(conversationId, chapterId, maxPlayerTurns);
        this._currentState = new StartingConversationState();

        this._saveConversationSnapshot(); // Save so that we can roll-back to the beginning aswell if needed (e.g. due content filter violation).

        // Keep track of the starting values of the character trackers.
        this._charactersInConversation.map(character => this._conversation.setStartingTrackersForCharacter(character.id, character.trackers.map(tracker => tracker.clone())));

        // Add previous messages from the scripted content to the conversation history.
        // Includes kickoff messages that provide context to the llm.
        this._conversation.addConversationHistory({
            gameConversationMessages: [],
            llmConversationMessages: [
                ...llmConversationHistory
            ],
        });

        // Conversation event listeners.
        this._conversation.on(Conversation.EVENT_MESSAGES, (evt) => {
            /** @type {Array<GameConversationMessage>} */
            const gameConversationMessages = evt.gameConversationMessages;
            const newConversationMessagesEvent = new NewConversationMessagesEvent(this._conversation, gameConversationMessages);
            this._dispatcher.dispatch(newConversationMessagesEvent.type, { data: newConversationMessagesEvent });
        });

        this._conversation.on(Conversation.EVENT_CHANGED_PHASE, (evt) => {
            /** @type {string} */
            const { phaseId, previousPhaseId } = evt;
            const conversationPhaseChangedEvent = new ConversationPhaseChangedEvent(ConversationPhaseChangedEvent.PERFORM, this._conversation, phaseId, previousPhaseId);
            this._dispatcher.dispatch(conversationPhaseChangedEvent.type, { data: conversationPhaseChangedEvent });
        });

        // Set initial phase.
        if (DebugModel.phase) {
            this._conversation.setPhase(DebugModel.phase);
        }

        // Notify that the conversation has started.
        const conversationStartedEvent = new ConversationStartedEvent(this._conversation);
        this._dispatcher.dispatch(conversationStartedEvent.type, { data: conversationStartedEvent });
    }

    async next() {
        console.log(`[ConversationPlayer] Executing state: ${this._currentState?.constructor.name}`);

        let nextState = null;
        try {
            nextState = await this._currentState.execute(this._chapterId, this._conversation, this._charactersInConversation, this._backendService, this._playerModel);
        } catch (ex) {
            console.error(`[${ConversationPlayer.name}] Error executing state. Rolling back to conversation snapshot.`, ex);

            this._currentState.destroy();

            // Load the conversation snapshot.
            this._loadConversationSnapshot();
            
            // Inform that the conversation has been rolled back.
            this._dispatcher.dispatch(ConversationRollbackEvent.PERFORM);
            
            // Dispatch the last AI messages, so that they show up in the UI.
            const lastAssistantMessages = [];
            for (let i = this._conversation.conversationHistoryForGame.length - 1; i >= 0; i--) {
                const message = this._conversation.conversationHistoryForGame[i];
                if (message.sender === GameMessageSender.PLAYER) {
                    break;
                }

                lastAssistantMessages.push(message);
            }
            const newConversationMessagesEvent = new NewConversationMessagesEvent(this._conversation, lastAssistantMessages);
            this._dispatcher.dispatch(newConversationMessagesEvent.type, { data: newConversationMessagesEvent });


            // Continue playing.
            return await this.next();
        }

        if (nextState && nextState != this._currentState) {
            this._addEventListenersToState(nextState);

            const conversationStateChangedEvent = new ConversationStateChangedEvent(this._conversation, nextState);
            this._dispatcher.dispatch(conversationStateChangedEvent.type, { data: conversationStateChangedEvent });
        }

        this._currentState.destroy();

        if (!this._conversation.hasEnded) {
            console.log(`[ConversationPlayer] Next state: ${nextState?.constructor.name}`);
            this._currentState = nextState;

            return await this.next();
        }

        this.end();
    }

    end() {
        console.log(`[ConversationPlayer] Conversation has ended.`);

        // Keep track of the ending values of the character trackers.
        this._charactersInConversation.forEach(character => {
            const trackers = character.trackers.map(tracker => tracker.clone());
            this._conversation.setEndingTrackersForCharacter(character.id, trackers);
        });

        const conversationEndedEvent = new ConversationEndedEvent(this._conversation);
        this._dispatcher.dispatch(conversationEndedEvent.type, { data: conversationEndedEvent });
    }

    /**
     * 
     * @param {string} message 
     */
    submitPlayerInput(message) {
        this._currentState?.handlePlayerInput(message);
    }

    _addEventListenersToState(state) {
        state.on(PlayerTurnConversationState.EVENT_CHARACTER_TRACKER_VALUE_CHANGED, this._handleCharacterTrackerValueChanged.bind(this));
        state.on(PlayerTurnConversationState.EVENT_PHASE_REASONING, this._handlePhaseReasoning.bind(this));
        state.on(ConversationState.SAVE_SNAPSHOT, this._saveConversationSnapshot.bind(this));
    }

    _handleCharacterTrackerValueChanged(evt) {
        /** @type {CharacterModel} */
        const character = evt.character;
        const trackerId = evt.trackerId;
        const oldValue = evt.oldValue;
        const newValue = evt.newValue;

        const characterTrackerEvent = new CharacterTrackerEvent(CharacterTrackerEvent.CHANGED, character, trackerId, oldValue, newValue);
        this._dispatcher.dispatch(characterTrackerEvent.type, { data: characterTrackerEvent });
    }

    _handlePhaseReasoning(evt) {
        const conversationPhaseReasoningEvent = new ConversationPhaseReasoningEvent(ConversationPhaseReasoningEvent.PERFORM, evt.reasoning);
        this._dispatcher.dispatch(conversationPhaseReasoningEvent.type, { data: conversationPhaseReasoningEvent });
    }

    _saveConversationSnapshot() {
        if (this._conversation === null || this._currentState === null) {
            console.error(`[${ConversationReflectionPlayer.name}] Cannot save conversation snapshot. Conversation or state is null.`);
            return;
        }

        this._conversationSnapshotData = this._conversation.toObject();
        this._conversationSnapshotState = this._currentState;

        // Save trackers.
        const characterIds = this._charactersInConversation.map(character => character.id);
        this._conversationCharacterTrackerSnapshot.clear();
        for (const characterId of characterIds) {
            const character = this._charactersInConversation.find(character => character.id === characterId);
            const characterTrackers = character.trackers.map(tracker => tracker.clone()); // NOTE: This clones the trackers.
            this._conversationCharacterTrackerSnapshot.set(characterId, characterTrackers);
        }
    }

    _loadConversationSnapshot() {
        this._conversation.load(this._conversationSnapshotData);
        this._currentState = this._conversationSnapshotState;
        this._currentState.reset();

        // Restore trackers.
        for (const character of this._charactersInConversation) {
            const trackersSnapshot = this._conversationCharacterTrackerSnapshot.get(character.id);
            for (const tracker of trackersSnapshot) {
                character.setTrackerValue(tracker.id, tracker.value);
            }
        }
    }

    get conversation() {
        return this._conversation;
    }
}